// scheduling/services/poolApi.js
// Client wrapper for /api/rprsPool (Job Pools, Trello #257).
//
// Mirrors schedApi's shape and reuses its cold-start retry for writes. Kept
// separate from schedApi because the pool is its own domain Function and
// deliberately does NOT participate in the Outlook reconcile — pool work never
// creates schedule entries, so there's nothing to mirror to a calendar.

import { fetchWithColdStartRetry } from './schedApi';

const POOL_BASE = '/api/rprsPool';

// Errors surface the server's message so the UI can show why a claim lost a race
// ("Someone else just claimed that job") rather than a generic failure. `status`
// is attached so callers can branch on 409 (lost race / FIFO) vs 403.
async function call(action, body = {}, { retry = true } = {}) {
  const url = `${POOL_BASE}?action=${encodeURIComponent(action)}`;
  const opts = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
  const r = retry
    ? await fetchWithColdStartRetry(url, opts, { label: `rprsPool(${action})` })
    : await fetch(url, opts);

  let json = null;
  try { json = await r.json(); } catch (_) { /* non-JSON error body */ }

  if (!r.ok) {
    const err = new Error((json && json.error) || `rprsPool(${action}) failed (${r.status})`);
    err.status = r.status;
    throw err;
  }
  // body.preview guard: previewSendToPool shares the 'send' action but writes
  // nothing, so it must not announce a change.
  if (MUTATING_ACTIONS.has(action) && !body.preview) notifyPoolChanged(action);
  return json;
}

// Anything that can change what the top-bar Job Pools indicator should show:
// availability (the steady dot) or who holds a job (the pulse + counts).
const MUTATING_ACTIONS = new Set([
  'send', 'claim', 'assign', 'checkin', 'checkout', 'update_capacity', 'request_staff',
]);

// AppShell loads its pool nav status once and otherwise polls every 60s, so without
// this your own check-in wouldn't light the dot until a refresh. Broadcasting the
// change closes that gap for the acting user; cross-user changes still arrive on the
// poll, since no event can cross browsers. Precedent: 'app:cold-start-retry'.
function notifyPoolChanged(action) {
  try {
    window.dispatchEvent(new CustomEvent('pool:changed', { detail: { action } }));
  } catch (_) { /* non-browser context (tests) — nothing to notify */ }
}

// The caller's local date, for anything where a calendar day matters. The server
// runs UTC, so after ~7pm Central its "today" is already tomorrow — every call
// that touches a date boundary sends this instead of letting SQL guess.
export function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── reads ───────────────────────────────────────────────────────────────────
export const fetchPools = () => call('list', {}, { retry: false });
export const fetchMyPooled = (limit) => call('my_pooled', { limit }, { retry: false });
export const fetchMyPoolHistory = (limit) => call('my_history', { limit }, { retry: false });
export const fetchPoolAvailability = (forPool) => call('availability', { forPool }, { retry: false });
export const fetchPoolConfig = () => call('config', {}, { retry: false });
export const fetchPoolAudit = (days) => call('audit', { days }, { retry: false });
// Tiny payload for the top-bar nav indicator — no job rows.
export const fetchPoolNavStatus = () => call('nav_status', {}, { retry: false });

// ── send to pool ────────────────────────────────────────────────────────────
// Two-step by design. previewSendToPool reports exactly which schedule entries
// clearing WOULD remove and writes nothing; sendToPool then re-derives that set
// server-side. The client never passes entry ids, so a stale preview can't widen
// what gets deleted.
export const previewSendToPool = ({ jobId, poolCode }) =>
  call('send', { jobId, poolCode, preview: true, today: localToday() }, { retry: false });

export const sendToPool = ({ jobId, poolCode, notes, clearEntries }) =>
  call('send', { jobId, poolCode, notes, clearEntries: !!clearEntries, today: localToday() });

// Open, poolable jobs not already waiting, by client name/code or job code/name — the
// Job Pools page's "Send a job" search, for admin staff sending jobs they don't lead.
export const searchPoolJobs = (q) => call('search_jobs', { q }, { retry: false });

export const updatePoolNotes = ({ poolJobId, notes }) =>
  call('update_notes', { poolJobId, notes });

// ── claim / assign ──────────────────────────────────────────────────────────
// No staffId on claim: the server records it against the authenticated caller.
export const claimPoolJob = (poolJobId) =>
  call('claim', { poolJobId, today: localToday() }, { retry: false });

export const assignPoolJob = ({ poolJobId, staffId }) =>
  call('assign', { poolJobId, staffId, today: localToday() }, { retry: false });

// ── availability ────────────────────────────────────────────────────────────
export const poolCheckIn = (capacityHours) => call('checkin', { capacityHours });
export const poolCheckOut = () => call('checkout', {});
export const poolUpdateCapacity = (capacityHours) => call('update_capacity', { capacityHours });

// ── admin config ────────────────────────────────────────────────────────────
// Batch-update rprs.PoolConfig. Admin-gated server-side. Returns the rebuilt rules
// plus eligible headcounts so the editor can show the effect immediately.
// Deliberately NOT in MUTATING_ACTIONS: config edits don't change your own
// availability or a job's ownership, so there's no nav indicator to refresh.
export const updatePoolConfig = (updates) => call('upsert_config', { updates });

// Ask an available staffer to pick up work when there's nothing pooled to assign.
// Notification only — no job, no PE write — and it clears their availability flag.
export const requestPoolStaff = ({ staffId, note }) => call('request_staff', { staffId, note });
